import { inject } from '@angular/core';
import { EMPTY, pipe } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  setAllEntities,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';

import { PdiAccount } from '@customer/models/pdi-account.model';
import { PdiAccountsService } from '@customer/services/pdi-accounts.service';

const ui = {
  isLoading: false,
  error: null as string | null,
  isCollectionLoaded: false,
};

export const PdiAccountsStore = signalStore(
  { providedIn: 'root' },
  withState(ui),
  withEntities<PdiAccount>(),
  withMethods((store, svc = inject(PdiAccountsService)) => {
    const streamAllAccounts = rxMethod<void>(
      pipe(
        switchMap(() => {
          if (store.isCollectionLoaded()) {
            return EMPTY;
          }
          return svc.streamAllAccounts().pipe(
            tap({
              next: accounts =>
                patchState(store, setAllEntities(accounts), {
                  isCollectionLoaded: true,
                  isLoading: false,
                  error: null,
                }),
              error: () => {},
            }),
            catchError(err => {
              patchState(store, {
                isLoading: false,
                error: err.message ?? 'Failed to stream PDI accounts',
              });
              return EMPTY;
            })
          );
        })
      )
    );

    function syncAccountContactFields(
      pdiAccountId: string,
      fields: { email: string; contactName: string }
    ): void {
      const existing = store.entities().find(a => a.id === pdiAccountId);
      if (!existing) {
        return;
      }
      patchState(
        store,
        updateEntity({
          id: pdiAccountId,
          changes: {
            email: fields.email,
            contactName: fields.contactName,
          },
        })
      );
    }

    return { streamAllAccounts, syncAccountContactFields };
  })
);
